import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { EdgeLabelRenderer, getBezierPath } from 'reactflow';
import { THEME } from '../../theme';

function arrowPolygon(x0, y0, tipX, tipY, size = 11) {
    const dx = tipX - x0;
    const dy = tipY - y0;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const bx = tipX - ux * size;
    const by = tipY - uy * size;
    const px = -uy * (size * 0.55);
    const py = ux * (size * 0.55);
    return `${tipX},${tipY} ${bx + px},${by + py} ${bx - px},${by - py}`;
}

function usePathArrow(pathD, tipAt = 0.86, backAt = 0.74) {
    const pathRef = useRef(null);
    const [arrow, setArrow] = useState(null);

    useLayoutEffect(() => {
        const el = pathRef.current;
        if (!el || !pathD) {
            setArrow(null);
            return;
        }
        const len = el.getTotalLength();
        if (!len) {
            setArrow(null);
            return;
        }
        const tip = el.getPointAtLength(len * tipAt);
        const back = el.getPointAtLength(len * backAt);
        setArrow({ tipX: tip.x, tipY: tip.y, backX: back.x, backY: back.y });
    }, [pathD, tipAt, backAt]);

    return { pathRef, arrow };
}

/** Sample packet positions along the rendered bezier for flow / route animation. */
function usePathPackets(pathD, count, phase) {
    const pathRef = useRef(null);
    const [pts, setPts] = useState([]);

    useLayoutEffect(() => {
        const el = pathRef.current;
        if (!el || !pathD || count <= 0) {
            setPts([]);
            return;
        }
        const len = el.getTotalLength();
        if (!len) {
            setPts([]);
            return;
        }
        const next = [];
        for (let i = 0; i < count; i++) {
            const t = (phase + i / count) % 1;
            const p = el.getPointAtLength(len * t);
            next.push({ x: p.x, y: p.y });
        }
        setPts(next);
    }, [pathD, count, phase]);

    return { packetPathRef: pathRef, packets: pts };
}

export default function LogisticsFlowEdge({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    label,
    data,
    selected,
}) {
    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        curvature: 0.28,
    });

    const { pathRef, arrow } = usePathArrow(edgePath, 0.86, 0.74);

    const state = data?.solutionState || 'off';
    const flowQty = Number(data?.flowQty) || 0;
    const flowStrength = data?.flowStrength ?? (flowQty > 0 ? Math.min(1, flowQty / 80) : 0);
    const routeColor = data?.routeColor;
    const packetPhase = data?.packetPhase ?? 0;
    const showPackets = Boolean(data?.showPackets) && (state === 'flow' || state === 'active' || state === 'route');

    const isActive = state === 'active';
    const isDone = state === 'done';
    const isFlow = state === 'flow';
    const isRoute = state === 'route';
    const isDim = state === 'dim';
    const isUpcoming = state === 'upcoming';

    let stroke = '#555';
    let strokeWidth = 1.6;
    let opacity = 0.9;
    let className = 'logistics-edge';

    if (selected) {
        stroke = '#38bdf8';
        strokeWidth = 3;
    } else if (isActive) {
        stroke = routeColor || '#facc15';
        strokeWidth = 3.5;
        className += ' logistics-edge--active';
    } else if (isDone) {
        stroke = routeColor || THEME.primary;
        strokeWidth = 3;
        className += ' logistics-edge--done';
    } else if (isUpcoming) {
        stroke = routeColor || THEME.primary;
        strokeWidth = 2;
        opacity = 0.35;
    } else if (isRoute) {
        stroke = routeColor || THEME.primary;
        strokeWidth = 2.8;
        className += ' logistics-edge--route';
    } else if (isFlow) {
        stroke = THEME.primary;
        strokeWidth = 2 + flowStrength * 3.2;
        opacity = 0.55 + flowStrength * 0.45;
        className += ' logistics-edge--flow';
    } else if (isDim) {
        className += ' logistics-edge--dim';
        opacity = 0.22;
    }

    const highlight = isActive || isDone || isFlow || isRoute || selected;
    const packetCount = showPackets ? (isActive ? 2 : Math.max(1, Math.min(4, Math.round(1 + flowStrength * 3)))) : 0;
    const { packetPathRef, packets } = usePathPackets(edgePath, packetCount, packetPhase);

    const displayLabel = useMemo(() => {
        if (flowQty > 0 && (isFlow || isActive || isDone)) {
            const base = label != null && label !== '' ? String(label) : '';
            const qty = flowQty >= 10 ? flowQty.toFixed(0) : flowQty.toFixed(1);
            return base ? `${base} · q=${qty}` : `q=${qty}`;
        }
        return label;
    }, [flowQty, isFlow, isActive, isDone, label]);

    return (
        <g className={className} opacity={opacity}>
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={24}
                className="react-flow__edge-interaction"
            />
            {(isActive || isDone || isFlow) && (
                <path
                    d={edgePath}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={isActive ? 12 : 8}
                    opacity={isActive ? 0.35 : 0.18}
                    className="logistics-edge-glow"
                />
            )}
            {/* Measurement path for packets (invisible) */}
            <path ref={packetPathRef} d={edgePath} fill="none" stroke="none" />
            <path
                ref={pathRef}
                id={id}
                d={edgePath}
                fill="none"
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                className="logistics-edge-path"
            />
            {arrow && (
                <polygon
                    points={arrowPolygon(arrow.backX, arrow.backY, arrow.tipX, arrow.tipY, 11)}
                    fill={stroke}
                    className="logistics-arrow-head"
                />
            )}
            {packets.map((p, i) => (
                <circle
                    key={`${id}-pkt-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r={isActive ? 5.5 : 4 + flowStrength * 2}
                    fill={routeColor || THEME.primary}
                    opacity={0.95}
                />
            ))}
            {displayLabel != null && displayLabel !== '' && (
                <EdgeLabelRenderer>
                    <div
                        className={`nodrag nopan logistics-edge-label${highlight ? ' logistics-edge-label--on' : ''}`}
                        title="Double-click to edit cost"
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                            pointerEvents: 'all',
                            cursor: 'pointer',
                            borderColor: routeColor && highlight ? routeColor : undefined,
                            color: routeColor && highlight ? routeColor : undefined,
                        }}
                        onDoubleClick={(ev) => {
                            ev.stopPropagation();
                            data?.onEditWeight?.();
                        }}
                    >
                        {displayLabel}
                    </div>
                </EdgeLabelRenderer>
            )}
        </g>
    );
}
